/*
 * @Description:
 * @Author: Leyton
 * @LastEditors: Leyton
 * @LastEditTime: 2022-08-02 11:23:12
 * @FilePath: /91qs_answer_frontpro/src/pages/slider/BaseSlider/index.js
 */
import { contexData } from "@/store";
import { StarFilled } from "@ant-design/icons";
import { Popover } from "antd";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { isEmpty } from "lodash";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import CarouselComponent from "@/components/CarouselComponent";
import "./index.less";

function isOverflown(element) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

const DESCRIPTION_SEPARATOR = "======";

const stripHtml = (text = "") => {
  return text
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
};

const splitDescription = (description = "", stripHtmlEnabled = false) => {
  const separatorIndex = description.indexOf(DESCRIPTION_SEPARATOR);
  if (separatorIndex === -1) {
    const display = stripHtmlEnabled ? stripHtml(description) : description;
    return { display, hover: "", hasSeparator: false };
  }
  const rawDisplay = description.slice(0, separatorIndex).trim();
  const rawHover = description.slice(separatorIndex + DESCRIPTION_SEPARATOR.length).trim();
  const display = stripHtmlEnabled ? stripHtml(rawDisplay) : rawDisplay;
  const hover = stripHtmlEnabled ? stripHtml(rawHover) : rawHover;
  return { display, hover, hasSeparator: true };
};

const toNumber = (value) => {
  if (value === undefined || value === null || value === "") return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
};

const getSliderValueProps = (valueMap, childId, defaultStart) => {
  const currentValue = valueMap?.[childId];
  if (currentValue === undefined || currentValue === null) {
    if (defaultStart === undefined || defaultStart === null) return {};
    return { defaultValue: defaultStart };
  }
  return { value: currentValue };
};

const getSliderRangeValueProps = (currentValue, defaultStart) => {
  if (defaultStart === undefined || defaultStart === null) return {};
  if (currentValue === undefined || currentValue === null) {
    return { defaultValue: [defaultStart, defaultStart] };
  }
  const minVal = Math.min(defaultStart, currentValue);
  const maxVal = Math.max(defaultStart, currentValue);
  return { value: [minVal, maxVal] };
};

const resolveRangeCurrent = (rangeValues, startPoint, currentValue, prevRange) => {
  if (!Array.isArray(rangeValues)) return rangeValues;
  const start = toNumber(startPoint);
  const a = toNumber(rangeValues[0]);
  const b = toNumber(rangeValues[1]);
  const current = toNumber(currentValue);
  if (start === undefined) return b ?? a;
  if (a === start && b !== start) return b;
  if (b === start && a !== start) return a;
  if (Array.isArray(prevRange)) {
    const pa = toNumber(prevRange[0]);
    const pb = toNumber(prevRange[1]);
    if (a !== pa && b === pb) return a;
    if (b !== pb && a === pa) return b;
  }
  if (current !== undefined) {
    if (a === current && b !== current) return b;
    if (b === current && a !== current) return a;
  }
  if (a !== undefined && b !== undefined) {
    const min = Math.min(a, b);
    const max = Math.max(a, b);
    if (start >= min && start <= max) {
      const distA = Math.abs(a - start);
      const distB = Math.abs(b - start);
      return distA >= distB ? a : b;
    }
  }
  return b;
};

const BaseSlider = ({ info, answer, changeAnswer, themeId, isMinimalTheme }) => {
  const [value, setValue] = useState({});
  const [initilalized, setInitialized] = useState(false);

  const [topVal, setTopVal] = useState({});
  const lastRangeRef = useRef({});

  const { infoContex } = useContext(contexData);
  const type = Number(info.preferences?.childPrefer?.optionForm ?? 0);
  const isCardForm = type === 2 || type === 3;
  const isStarForm = type === 1 || type === 3;
  const resolvedMinimalTheme = Number(themeId) === 4 || !!isMinimalTheme;
  const isNewScoreRadio = info?.model === "newscore_radio";
  const showText = Number(info.preferences?.childPrefer?.showText ?? 0) === 1;
  const defaultIndex = Number(info.preferences?.childPrefer?.defaultIndex ?? 0);
  const compactSliderSpacing = resolvedMinimalTheme ;
  const questionForm = info.preferences?.childPrefer?.questionForm ?? 1; // 0: border, 1: no border
  const cardHasBorder = isCardForm && questionForm === 0;
  const cardWrapperStyle = cardHasBorder
    ? {
        border: "1px solid #bbd7f9",
        // borderBottom: "none",
        borderRadius: 4,
        // padding: 10,
        background: "#fff",
        marginBottom: 12,
        boxSizing: "border-box",
        overflow: "hidden",
      }
    : { marginBottom: 12 };
  const answerValue = useMemo(() => answer[info.id] || answer || {}, [answer, info.id]);
  const cardChilds = useMemo(
    () => (info.childs || []).filter((child) => +child.moduleOption === 0 && !child.hide),
    [info.childs]
  );
  const [carouselCtrl, setCarouselCtrl] = useState(null);

  const resolveOptionByValue = (child, currentValue) => {
    if (!child || currentValue === undefined || currentValue === null) return null;
    const normalizedValue = info.optionReverse === 2 && type === 0 ? Math.abs(currentValue) : currentValue;
    return (child.options || []).find((o) => o.point == normalizedValue) || null;
  };

  const getDefaultStartPoint = (child) => {
    if (!isNewScoreRadio || defaultIndex === 0) return undefined;
    const pointsByOrder = (child.options || [])
      .map((o) => toNumber(o.point))
      .filter((p) => p !== undefined);
    if (pointsByOrder.length === 0) return undefined;

    // 星星评分按视觉顺序(选项顺序)决定左右，不用数值大小判断
    if (isStarForm) {
      let targetByOrder;
      if (defaultIndex === 1) targetByOrder = pointsByOrder[0];
      if (defaultIndex === 2) targetByOrder = pointsByOrder[pointsByOrder.length - 1];
      if (defaultIndex === 3) {
        targetByOrder = pointsByOrder[Math.floor((pointsByOrder.length - 1) / 2)];
      }
      return targetByOrder;
    }

    const minPoint = Math.min(...pointsByOrder);
    const maxPoint = Math.max(...pointsByOrder);
    const isReverseAxis = info.optionReverse === 2;
    let target;
    if (defaultIndex === 1) target = isReverseAxis ? maxPoint : minPoint;
    if (defaultIndex === 2) target = isReverseAxis ? minPoint : maxPoint;
    if (defaultIndex === 3) {
      const sorted = [...pointsByOrder].sort((a, b) => a - b);
      target = sorted[Math.floor((sorted.length - 1) / 2)];
    }
    if (target === undefined) return undefined;
    if (isReverseAxis && type === 0) target = -target;
    return target;
  };

  const shouldHideHandle = (child, currentValue) => {
    if (!isNewScoreRadio || defaultIndex !== 0) return false;
    const hasAnswer = isChildAnswered(answerValue, child.id);
    if (hasAnswer) return false;
    const hasValue = value?.[child.id] !== undefined && value?.[child.id] !== null;
    if (hasValue) return false;
    return true;
  };

  const getTooltipContent = (option, fallbackPoint) => {
    const pointText = fallbackPoint ?? "";
    if (!option) return String(pointText);
    const { hover } = splitDescription(option.description || "", isNewScoreRadio);
    if (showText) return hover || String(pointText);
    return String(pointText);
  };

  const isCurrentCard = (childIndex) => {
    if (!isCardForm) return true;
    return Number(carouselCtrl?.current ?? 0) === childIndex;
  };

  const getTooltipContainer = (triggerNode) => {
    return (
      triggerNode?.closest?.(".slider") ||
      triggerNode?.parentElement ||
      document.body
    );
  };

  const renderSelectedTooltip = (node, tooltipContent, isVisible, tooltipKey) => {
    if (!tooltipContent) return node;
    return (
      <Popover
        key={tooltipKey}
        content={tooltipContent}
        getPopupContainer={getTooltipContainer}
        placement="top"
        autoAdjustOverflow={false}
        overlayStyle={{ pointerEvents: "none" }}
        trigger={window.innerWidth < 500 ? "click" : "hover"}
        visible={isVisible ? true : false}
        destroyTooltipOnHide
      >
        {React.cloneElement(node, { key: tooltipKey })}
      </Popover>
    );
  };

  const isChildAnswered = (answersMap, childId) => {
    const childAnswer = answersMap?.[childId];
    return !!(childAnswer && Object.keys(childAnswer).length);
  };

  const handleAutoAdvance = (updatedAnswer) => {
    if (!isCardForm || cardChilds.length === 0) return;
    const answeredCount = cardChilds.reduce((count, child) => {
      return count + (isChildAnswered(updatedAnswer, child.id) ? 1 : 0);
    }, 0);
    const totalCount = cardChilds.length;

    const { ref, current } = carouselCtrl || {};
    const nextIndex = (current || 0) + 1;
    if (ref?.current && nextIndex < totalCount) {
      setCarouselCtrl((old) => (old ? { ...old, current: nextIndex } : old));
      setTimeout(() => {
        if (ref && ref.current && typeof ref.current.slickGoTo === "function") {
          ref.current.slickGoTo(nextIndex);
        }
      }, 200);
    }
  };

  const onChange = (point, child, negtive = false) => {
    const { id } = child;
    setValue((old) => ({ ...old, [id]: point }));
    const option = child.options.find(
      (o) => o.point === (negtive ? -point : point)
    );
    if (!isEmpty(option)) {
      setInitialized(true);
      const updatedAnswer = { ...answerValue, [id]: { [option.id]: null } };
      changeAnswer(updatedAnswer);
      handleAutoAdvance(updatedAnswer);
    }
  };

  useEffect(() => {
    if (!isEmpty(answerValue) && !initilalized) {
      try {
        const obj = answerValue;
        const keys = Object.keys(obj);
        const v = {};
        for (let i = 0; i < keys.length; i++) {
          const cid = keys[i];
          const [oid] = Object.keys(obj[cid]);
          const opt = info.childs
            .find((c) => c.id === cid)
            ?.options.find((o) => o.id === oid);
          const nCondition = info.optionReverse === 2 && type === 0;
          if (opt) v[cid] = nCondition ? -opt.point : opt.point;
        }
        setValue(v);
      } catch (error) {
        console.log(error.message);
      }
    }
  }, [answerValue, info]);

  // defaultIndex only controls the slider start position, not selection.

  useEffect(() => {
    try {
      const handles = document.getElementsByClassName("ant-slider-handle");
      for (let index = 0; index < handles.length; index++) {
        const element = handles[index];

        element.style.borderColor = infoContex.themeColor;
      }

      const tracks = document.getElementsByClassName("ant-slider-track");
      for (let index = 0; index < tracks.length; index++) {
        const element = tracks[index];

        element.style.backgroundColor = infoContex.themeColor;
      }
    } catch (error) { }
  }, [infoContex.themeColor]);

  useEffect(() => {
    if (isStarForm) {
      try {
        info.childs.map((child) => {
          const ele = document.getElementById("star" + child.id);
          const b = isOverflown(ele);
          if (b) {
            ele.style.transform = "scale(0.9)";
          }
        });
      } catch (error) { }
    }
  });

  useEffect(() => {
    const interval = setInterval(() => {
      try {
        const elements = document.getElementsByClassName("ant-tooltip-inner");
        Array.from(elements).forEach((ele) => {
          ele.innerHTML = Math.abs(Number(ele.innerHTML));
        });
      } catch (error) { }
    }, 10);
    return () => {
      clearInterval(interval);
    };
  }, []);
  console.log(111111);

  const renderWithCard = (nodes) => {
    if (!isCardForm) return nodes;
    return (
      <CarouselComponent
        info={info}
        colData={cardChilds}
        cardOptionFormEnable
        callback={(dom, autoplayFn, current, extra = {}) => {
          setCarouselCtrl({
            ref: dom,
            autoplayFn,
            current,
            goNext: extra.goNext,
          });
        }}
      >
        {React.Children.map(nodes, (childNode, index) => {
          if (!childNode) return childNode;
          return (
            <div
              key={index}
            >
            <div style={cardWrapperStyle}>{childNode}</div>
            </div>
          );
        })}
      </CarouselComponent>
    );
  };

  const sliderChilds = isCardForm ? cardChilds : info.childs;

  if (!isStarForm) {
    // 标尺
    window.history.pushState(null, null, document.URL);

    if (info.optionReverse === 2) {
      const marks = {};
      info.options.map((o, i) => {
        const { display } = isNewScoreRadio ? splitDescription(o.description, true) : { display: o.description };
        const point = toNumber(o.point);
        if (point === undefined) return;
        marks[-point] = (
          <p
            style={{
              wordBreak: "break-word",
              lineHeight: "1.1em",
              width:
                display.replace(/<[^>]+>/g, "").length > 1 ? "3em" : 0,
            }}
            dangerouslySetInnerHTML={{ __html: display }}
          ></p>
        );
      });
      const nodes = sliderChilds.map((child, childIndex) => {
        if (child.hide) return null;
        const startPoint = getDefaultStartPoint(child);
        const useStartRange = isNewScoreRadio && startPoint !== undefined && startPoint !== null;
        return (
          <div key={child.id}>
            <div
              style={{
                padding: "8px 20px",
                color: infoContex?.preferences?.sonColour,
                background: cardHasBorder ? "#bbd7f9" : undefined,
                borderRadius: cardHasBorder ? "4px 4px 0 0" : undefined,
              }}
              dangerouslySetInnerHTML={{ __html: child.description }}
            />
            {!compactSliderSpacing && <br />}
            <div
              className="slider"
              style={{
                padding:
                  (compactSliderSpacing ? "10px " : "30px ") +
                  (window.innerWidth < 500 ? "20px" : "60px"),
                overflow: "visible",
                paddingBottom: compactSliderSpacing ? 10 : 30,
                marginBottom: 16,
              }}
            >
              <Slider
                marks={marks}
                {...(isNewScoreRadio
                  ? useStartRange
                    ? getSliderRangeValueProps(value[child.id], startPoint)
                    : getSliderValueProps(value, child.id, startPoint)
                  : { value: value[child.id], defaultValue: value[child.id] })}
                onChange={(nextRange) => {
                  const prevRange = lastRangeRef.current[child.id];
                  if (Array.isArray(nextRange)) {
                    lastRangeRef.current[child.id] = nextRange;
                  }
                  onChange(
                    resolveRangeCurrent(
                      nextRange,
                      startPoint,
                      value?.[child.id],
                      prevRange
                    ),
                    child,
                    true
                  );
                }}
              min={-Math.max(...child.options.map((a) => a.point))}
              max={-Math.min(...child.options.map((a) => a.point))}
              range={useStartRange ? true : undefined}
              draggableTrack={useStartRange ? false : true}
              allowCross={useStartRange ? false : undefined}
                handleRender={
                  isNewScoreRadio
                    ? (node, props) => {
                        if (
                          useStartRange &&
                          toNumber(props.value) === toNumber(startPoint)
                        ) {
                          return React.cloneElement(node, {
                            style: { ...node.props.style, display: "none" },
                          });
                        }
                        const selectedOption = resolveOptionByValue(child, props.value);
                        const displayPoint =
                          selectedOption?.point ??
                          (info.optionReverse === 2 && type === 0
                            ? Math.abs(props.value)
                            : props.value);
                        const tooltipContent = getTooltipContent(selectedOption, displayPoint);
                        const isSelected =
                          isChildAnswered(answerValue, child.id) ||
                          (value?.[child.id] !== undefined && value?.[child.id] !== null);
                        const isTooltipVisible = isSelected && isCurrentCard(childIndex);
                        const tooltipKey = `slider-tooltip-${child.id}-${childIndex}-${carouselCtrl?.current ?? 0}-${props.index ?? 0}-${displayPoint}-${tooltipContent}`;
                        if (shouldHideHandle(child, props.value)) {
                          return React.cloneElement(node, {
                            style: { ...node.props.style, display: "none" },
                          });
                        }
                        return renderSelectedTooltip(
                          node,
                          tooltipContent,
                          isTooltipVisible,
                          tooltipKey
                        );
                      }
                    : undefined
                }
              />
            </div>
            {!compactSliderSpacing &&
              (info.options[0].description.replace(/<[^>]+>/g, "").length > 5 ? (
                <>
                  <br />
                  <br />
                  <br />
                  <br />
                </>
              ) : (
                <>
                  <br />
                  <br />
                </>
              ))}
          </div>
        );
      });
      return renderWithCard(nodes);
    }

    const marks = {};

    info.options.map((o, i) => {
      const { display } = isNewScoreRadio ? splitDescription(o.description, true) : { display: o.description };
      const point = toNumber(o.point);
      if (point === undefined) return;
      marks[point] = <div dangerouslySetInnerHTML={{ __html: display }}></div>;
    });
    const optionPoints = info.options
      .map((o) => toNumber(o.point))
      .filter((p) => p !== undefined);
    const minPoint = optionPoints.length ? Math.min(...optionPoints) : 0;
    const maxPoint = optionPoints.length ? Math.max(...optionPoints) : 0;

    const nodes = sliderChilds.map((child, childIndex) => {
      if (child.hide) return null;
      const startPoint = getDefaultStartPoint(child);
      const useStartRange = isNewScoreRadio && startPoint !== undefined && startPoint !== null;

      if (+child.moduleOption === 1) {
        return (
          <div
            key={child.id}
            className="q-content-module"
            style={{
              background: "#bec6cd",
              margin: "15px",
            }}
            dangerouslySetInnerHTML={{
              __html: `  <div style="margin-left:5px">
                  ${child.description
                    .replace(/<p><\/p>/g, "<br />")
                    .replace(/\n\n/g, "<br />")
                    .replace(/\s{2}/g, "&nbsp;&nbsp;")}
                  
                  </div>`,
            }}
          />
        );
      }

      return (
        <div
          key={child.id}
          style={{
            paddingBottom:
              compactSliderSpacing
                ? "0px"
                : info.options[0].description.replace(/<[^>]+>/g, "").length > 5
                ? "80px"
                : "40px",
          }}
        >
          <div
            style={{
              padding: "8px 20px",
              color: infoContex?.preferences?.sonColour,
              background: cardHasBorder ? "#bbd7f9" : undefined,
              borderRadius: cardHasBorder ? "4px 4px 0 0" : undefined,
            }}
            dangerouslySetInnerHTML={{ __html: child.description }}
          />
          {!compactSliderSpacing && <br />}
            <div
              className="slider"
              style={{
                padding:
                  (compactSliderSpacing ? "32px " : "30px ") +
                  (window.innerWidth < 500 ? "20px" : "60px"),
                position: "relative",
                overflow: "visible",
                paddingBottom: compactSliderSpacing ? 0 : 30,
                marginBottom: compactSliderSpacing ? 30 : 12,
            }}
          >
            <Slider
              marks={marks}
              step={null}
              handleRender={(node, props) => {
                if (isNewScoreRadio) {
                  if (
                    useStartRange &&
                    toNumber(props.value) === toNumber(startPoint)
                  ) {
                    return React.cloneElement(node, {
                      style: { ...node.props.style, display: "none" },
                    });
                  }
                  const selectedOption = resolveOptionByValue(child, props.value);
                  const displayPoint =
                    selectedOption?.point ??
                    (info.optionReverse === 2 && type === 0
                      ? Math.abs(props.value)
                      : props.value);
                  const tooltipContent = getTooltipContent(selectedOption, displayPoint);
                  const isSelected =
                    isChildAnswered(answerValue, child.id) ||
                    (value?.[child.id] !== undefined && value?.[child.id] !== null);
                  const isTooltipVisible = isSelected && isCurrentCard(childIndex);
                  const tooltipKey = `slider-tooltip-${child.id}-${childIndex}-${carouselCtrl?.current ?? 0}-${props.index ?? 0}-${displayPoint}-${tooltipContent}`;
                  if (shouldHideHandle(child, props.value)) {
                    return React.cloneElement(node, {
                      style: { ...node.props.style, display: "none" },
                    });
                  }
                  return renderSelectedTooltip(
                    node,
                    tooltipContent,
                    isTooltipVisible,
                    tooltipKey
                  );
                }
                return (
                  <div style={{}}>
                    <div
                      style={{
                        background: "#000000bf",
                        position: "absolute",
                        top: -30,
                        width: "30px",
                        textAlign: "center",
                        color: "#fff",
                        ...node.props.style,
                      }}
                    >
                      {JSON.stringify(value[child.id])}
                    </div>
                    {node}
                  </div>
                );
              }}
              {...(isNewScoreRadio
                ? useStartRange
                  ? getSliderRangeValueProps(value[child.id], startPoint)
                  : getSliderValueProps(value, child.id, startPoint)
                : { value: value[child.id], defaultValue: value[child.id] })}
              onChange={(nextRange) => {
                const prevRange = lastRangeRef.current[child.id];
                if (Array.isArray(nextRange)) {
                  lastRangeRef.current[child.id] = nextRange;
                }
                onChange(
                  resolveRangeCurrent(
                    nextRange,
                    startPoint,
                    value?.[child.id],
                    prevRange
                  ),
                  child
                );
              }}
              min={minPoint}
              max={maxPoint}
              range={useStartRange ? true : undefined}
              draggableTrack={useStartRange ? false : true}
              allowCross={useStartRange ? false : undefined}
            />
          </div>
          <style>{`
              .rc-slider-mark-text{
                width: 50px;
              }
              `}</style>
        </div>
      );
    });
    return renderWithCard(nodes);
  }

  const starChilds = isCardForm ? cardChilds : info.childs;
  // 星星
  return renderWithCard(
    starChilds.map((child, childIndex) => {
      if (child.hide) return null;
      return (
        <div key={child.id} id={"star" + child.id}>
          <div
            style={{
              padding: "8px 20px",
              color: infoContex?.preferences?.sonColour,
              background: cardHasBorder ? "#bbd7f9" : undefined,
              borderRadius: cardHasBorder ? "4px 4px 0 0" : undefined,
            }}
            dangerouslySetInnerHTML={{ __html: child.description }}
          />
          <div
            style={{
              display: "flex",
              alignContent: "center",
              textAlign: "center",
              width: !isMobile() && "70%",
            }}
          >
            {child.options.map((opt, i) => {
              const point = value[child.id];
              const option = child.options.find((o) => o.point == point) || {};
              let color = "";
              const activeColor = infoContex.themeColor;
              const startPoint = getDefaultStartPoint(child);
              const hasSelection = point !== undefined && point !== null;
              if (isNewScoreRadio && startPoint !== undefined && hasSelection) {
                const minRange = Math.min(startPoint, point);
                const maxRange = Math.max(startPoint, point);
                color =
                  opt.point >= minRange && opt.point <= maxRange ? activeColor : "#eee";
              } else if (info.optionReverse === 2) {
                color = opt.point >= option.point ? activeColor : "#eee";
              } else {
                color = opt.point <= option.point ? activeColor : "#eee";
              }

              if (!isNewScoreRadio) {
                return (
                  <div
                    key={opt.id}
                    style={
                      isMobile()
                        ? {
                            textAlign: "center",
                            flex: window.innerWidth < 500 ? "auto" : "none",
                            marginTop: 10,
                            width: (100 / child.options.length) + "%",
                            paddingLeft: window.innerWidth < 500 ? 0 : 30,
                          }
                        : {
                            textAlign: "center",
                            width: (100 / child.options.length) + "%",

                            marginTop: 10,
                            paddingLeft: window.innerWidth < 500 ? 0 : 30,
                          }
                    }
                  >
                    <Popover
                      content={"" + opt.point}
                      trigger={window.innerWidth < 500 ? "click" : "hover"}
                    >
                      <StarFilled
                        style={{
                          color,
                          fontSize: 20,
                          marginTop: 10,
                          display: "inline",
                          verticalAlign: "bottom",
                          bottom: 0,
                        }}
                        onClick={() => onChange(opt.point, child)}
                      />
                    </Popover>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: opt.description + "",
                      }}
                    />
                  </div>
                );
              }

              const { display } = splitDescription(opt.description + "", isNewScoreRadio);
              const isSelected = opt.point == point;
              const isTooltipVisible = isSelected && isCurrentCard(childIndex);
              const tooltipContent = isNewScoreRadio
                ? getTooltipContent(opt, opt.point)
                : "";

              return (
                <div
                  key={opt.id}
                  style={
                    isMobile()
                      ? {
                          textAlign: "center",
                          flex: window.innerWidth < 500 ? "auto" : "none",
                          marginTop: 10,
                          width: (100 / child.options.length) + "%",
                          paddingLeft: window.innerWidth < 500 ? 0 : 30,
                        }
                      : {
                          textAlign: "center",
                          width: (100 / child.options.length) + "%",

                          marginTop: 10,
                          paddingLeft: window.innerWidth < 500 ? 0 : 30,
                        }
                  }
                >
                  {tooltipContent ? (
                    <Popover
                      content={tooltipContent}
                      getPopupContainer={getTooltipContainer}
                      placement="top"
                      autoAdjustOverflow={false}
                      overlayStyle={{ pointerEvents: "none" }}
                      trigger={window.innerWidth < 500 ? "click" : "hover"}
                      visible={isTooltipVisible ? true : false}
                      destroyTooltipOnHide
                    >
                      <StarFilled
                        style={{
                          color,
                          fontSize: 20,
                          marginTop: 10,
                          display: "inline",
                          verticalAlign: "bottom",
                          bottom: 0,
                        }}
                        onClick={() => onChange(opt.point, child)}
                      />
                    </Popover>
                  ) : (
                    <StarFilled
                      style={{
                        color,
                        fontSize: 20,
                        marginTop: 10,
                        display: "inline",
                        verticalAlign: "bottom",
                        bottom: 0,
                      }}
                      onClick={() => onChange(opt.point, child)}
                    />
                  )}
                  <div
                    dangerouslySetInnerHTML={{
                      __html: display + "",
                    }}
                  />
                </div>
              );
            })}
          </div>
          <br />
        </div>
      );
    })
  );
};

export default BaseSlider;
// 判断是否为移动设备
function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}
